import {
  ApprovalDecision,
  DoBoxEntryKind,
  DoBoxSpecialBucketKey,
  InboxEntry,
} from '@this+that/models';

import {
  DoBoxViewFilters,
  matchesDoBoxAttributeFilters,
} from './matchesDoBoxViewFilters';
import { CardStatusFilter } from '../../../../components/DoBoxFilters/types';
import { BucketKey } from '../../../../store/Inbox/InboxEntries/types';

type BucketClassifiableEntry = Pick<
  InboxEntry,
  | 'taskSubgroupId'
  | 'entryKind'
  | 'isSuggestion'
  | 'resolvedAt'
  | 'approvalDecision'
  | 'taskLabelIds'
  | 'participantIds'
  | 'priority'
  | 'dueAt'
>;

const CARD_STATUS_FILTER_TO_APPROVAL_DECISION: Partial<
  Record<CardStatusFilter, ApprovalDecision>
> = {
  [CardStatusFilter.APPROVED]: ApprovalDecision.APPROVED,
  [CardStatusFilter.REJECTED]: ApprovalDecision.REJECTED,
};

/**
 * Mirrors `getInitialDoBoxEntries`' backend bucket classification precedence
 * exactly: a subgroup assignment always wins, then APPROVAL, then suggestion,
 * else BACKLOG. Keep in sync with the backend if that precedence ever changes.
 */
export const resolveBucketKeyForEntry = (
  entry: BucketClassifiableEntry,
): BucketKey => {
  if (entry.taskSubgroupId) {
    return entry.taskSubgroupId;
  }
  if (entry.entryKind === DoBoxEntryKind.APPROVAL) {
    return DoBoxSpecialBucketKey.APPROVALS;
  }
  if (entry.isSuggestion === true) {
    return DoBoxSpecialBucketKey.SUGGESTIONS;
  }
  return DoBoxSpecialBucketKey.BACKLOG;
};

// Mirrors useDoBoxFilters' resolved-visibility rules: an approval with a
// decision filter (APPROVED/REJECTED) only shows when its decision matches.
// Otherwise it follows the same UNRESOLVED/RESOLVED/ALL semantics as every
// other entry (RESOLVED requires resolvedAt, UNRESOLVED requires it absent,
// ALL always shows) so a pending approval still resolves to the APPROVALS
// bucket under UNRESOLVED/ALL — required for bucketTotals delta-accounting
// to stay in sync.
const isEntryResolvedVisible = (
  entry: BucketClassifiableEntry,
  selectedCardStatusFilter: CardStatusFilter,
): boolean => {
  if (entry.entryKind === DoBoxEntryKind.APPROVAL) {
    const decisionFilter =
      CARD_STATUS_FILTER_TO_APPROVAL_DECISION[selectedCardStatusFilter];
    if (decisionFilter) {
      return entry.approvalDecision === decisionFilter;
    }
    if (selectedCardStatusFilter === CardStatusFilter.ALL) {
      return true;
    }
    const isResolved = Boolean(entry.resolvedAt);
    return selectedCardStatusFilter === CardStatusFilter.RESOLVED
      ? isResolved
      : !isResolved;
  }
  if (entry.isSuggestion === true) {
    return true;
  }
  if (selectedCardStatusFilter === CardStatusFilter.ALL) {
    return true;
  }
  const isResolved = Boolean(entry.resolvedAt);
  return selectedCardStatusFilter === CardStatusFilter.RESOLVED
    ? isResolved
    : !isResolved;
};

/**
 * Resolves the bucket an entry counts toward in the CURRENT view, or `null`
 * when the card-status filter or a label/member/priority/due-date filter
 * excludes it (the server filters every bucket the same way). Use this instead
 * of `resolveBucketKeyForEntry` alone whenever a count must reflect what the
 * user can actually see.
 */
export const resolveViewBucketKeyForEntry = (
  entry: BucketClassifiableEntry,
  filters: DoBoxViewFilters,
): BucketKey | null =>
  isEntryResolvedVisible(entry, filters.selectedCardStatusFilter) &&
  matchesDoBoxAttributeFilters(entry, filters)
    ? resolveBucketKeyForEntry(entry)
    : null;
